/**
 * Chunky UI primitives (DESIGN-client §7 "Chunky look"): Panel, Button, Toggle, Slider,
 * Segmented, Avatar, Sheet, Toast. Pure DOM + theme.css classes; no Pixi, no store.
 */
import { type ButtonHTMLAttributes, type CSSProperties, type ReactNode, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/* ---------- Panel ---------- */
export interface PanelProps {
  children: ReactNode;
  felt?: boolean;
  flat?: boolean;
  tight?: boolean;
  className?: string;
  style?: CSSProperties;
  testId?: string;
}

export function Panel({ children, felt, flat, tight, className, style, testId }: PanelProps) {
  const cls = ['panel', felt && 'panel--felt', flat && 'panel--flat', tight && 'panel--tight', className]
    .filter(Boolean)
    .join(' ');
  return (
    <div className={cls} style={style} data-testid={testId}>
      {children}
    </div>
  );
}

/* ---------- Button ---------- */
export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: 'md' | 'lg';
  icon?: boolean;
  block?: boolean;
  testId?: string;
}

export function Button({ variant = 'secondary', size = 'md', icon, block, className, testId, type = 'button', ...rest }: ButtonProps) {
  const cls = ['btn', `btn--${variant}`, size === 'lg' && 'btn--lg', icon && 'btn--icon', block && 'btn--block', className]
    .filter(Boolean)
    .join(' ');
  return <button type={type} className={cls} data-testid={testId} {...rest} />;
}

/* ---------- Toggle ---------- */
export interface ToggleProps {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  testId?: string;
}

export function Toggle({ label, hint, checked, onChange, disabled, testId }: ToggleProps) {
  const id = useId();
  return (
    <div
      role="switch"
      id={id}
      aria-checked={checked}
      aria-disabled={disabled || undefined}
      aria-label={label}
      tabIndex={disabled ? -1 : 0}
      className="toggle"
      data-testid={testId}
      onClick={() => !disabled && onChange(!checked)}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          onChange(!checked);
        }
      }}
    >
      <span className="toggle__label">
        <span>{label}</span>
        {hint && <span className="toggle__hint">{hint}</span>}
      </span>
      <span className="toggle__track">
        <span className="toggle__knob" />
      </span>
    </div>
  );
}

/* ---------- Slider ---------- */
export interface SliderProps {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
  format?: (v: number) => string;
  testId?: string;
}

export function Slider({ label, value, onChange, min = 0, max = 1, step = 0.05, format, testId }: SliderProps) {
  const id = useId();
  return (
    <div className="slider">
      <div className="slider__head">
        <label htmlFor={id}>{label}</label>
        <span className="tnum muted">{format ? format(value) : `${Math.round(value * 100)}%`}</span>
      </div>
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        data-testid={testId}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </div>
  );
}

/* ---------- Segmented ---------- */
export interface SegmentedProps<T extends string> {
  value: T;
  options: ReadonlyArray<{ value: T; label: string }>;
  onChange: (v: T) => void;
  label?: string;
  testId?: string;
}

export function Segmented<T extends string>({ value, options, onChange, label, testId }: SegmentedProps<T>) {
  return (
    <div className="seg" role="group" aria-label={label} data-testid={testId}>
      {options.map((o) => (
        <button key={o.value} type="button" className="seg__opt" aria-pressed={o.value === value} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

/* ---------- Avatar ---------- */
export interface AvatarProps {
  name: string;
  src?: string | null;
  size?: number;
  team?: 0 | 1 | null;
  speaking?: boolean;
  muted?: boolean;
  bot?: boolean;
  className?: string;
  onLongPress?: () => void;
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]!;
  const last = parts.length > 1 ? parts[parts.length - 1]! : '';
  return (first[0]! + (last[0] ?? '')).toUpperCase();
}

export function Avatar({ name, src, size = 40, team = null, speaking, muted, bot, className, onLongPress }: AvatarProps) {
  const [broken, setBroken] = useState(false);
  // A ref, not state: the timer is not rendered, and holding it in state meant an Avatar that
  // unmounted mid-press (the player leaves the table) still fired `onLongPress` afterwards, which
  // muted a departed player with no UI left to unmute them.
  const timerRef = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (timerRef.current != null) window.clearTimeout(timerRef.current);
    },
    [],
  );
  const ring = team === 0 ? 'var(--team-0)' : team === 1 ? 'var(--team-1)' : 'transparent';
  const style: CSSProperties = { width: size, height: size, fontSize: Math.round(size * 0.38), ['--ring' as string]: ring };
  const start = () => {
    if (!onLongPress) return;
    timerRef.current = window.setTimeout(() => onLongPress(), 500);
  };
  const cancel = () => {
    if (timerRef.current != null) window.clearTimeout(timerRef.current);
    timerRef.current = null;
  };
  return (
    <span
      className={['avatar', speaking && 'avatar--speaking', className].filter(Boolean).join(' ')}
      style={style}
      title={name}
      aria-label={name}
      data-interactive={onLongPress ? '' : undefined}
      onPointerDown={start}
      onPointerUp={cancel}
      onPointerLeave={cancel}
      onPointerCancel={cancel}
      onContextMenu={(e) => {
        if (onLongPress) {
          e.preventDefault();
          onLongPress();
        }
      }}
    >
      {src && !broken ? <img src={src} alt="" onError={() => setBroken(true)} /> : initials(name)}
      {(muted || bot) && <span className="avatar__badge">{muted ? '🔇' : '🤖'}</span>}
    </span>
  );
}

/* ---------- Sheet ---------- */
export interface SheetProps {
  open: boolean;
  title?: string;
  onClose: () => void;
  children: ReactNode;
  testId?: string;
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Sheets currently open, innermost last. Escape and Tab must be handled by the topmost one only:
 * Settings can open Credits on top of itself, and with both listening, one Escape closed both and
 * Shift-Tab inside Credits was yanked back into the Settings panel by the outer trap.
 */
const sheetStack: symbol[] = [];

export function Sheet({ open, title, onClose, children, testId }: SheetProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const idRef = useRef<symbol>(Symbol('sheet'));

  // `onClose` is almost always an inline arrow at the call site, so its identity changes on every
  // render of the owner. Keeping it in the effect's deps made the trap tear down and reinstall on
  // each render — four times a second while a timer ticks in the HUD — which re-ran the focus
  // setup and snapped focus back to the first control mid-Tab, and re-announced the dialog to
  // screen readers at the same rate. The ref keeps the latest handler without re-running.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    const id = idRef.current;
    sheetStack.push(id);

    // Return focus where it came from: a sheet opened from the HUD must not dump the player at
    // the top of the document when it closes.
    const previouslyFocused = document.activeElement as HTMLElement | null;
    panel?.querySelector<HTMLElement>(FOCUSABLE)?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (sheetStack[sheetStack.length - 1] !== id) return;
      if (e.key === 'Escape') {
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab' || !panel) return;
      // Trap: a modal that lets Tab wander into the page behind it is unusable with a keyboard
      // and unreadable with a screen reader.
      const items = [...panel.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => el.offsetParent !== null);
      if (items.length === 0) return;
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const active = document.activeElement;
      if (e.shiftKey && (active === first || !panel.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      const at = sheetStack.lastIndexOf(id);
      if (at >= 0) sheetStack.splice(at, 1);
      previouslyFocused?.focus?.();
    };
  }, [open]);
  if (!open) return null;
  // Portalled to <body>: the HUD that opens most sheets is `pointer-events:none`, and that
  // property inherits, so a sheet rendered in place would let the Pixi canvas eat every tap.
  return createPortal(
    <div className="sheet-backdrop" onClick={onClose} data-testid={testId ? `${testId}-backdrop` : undefined}>
      <div
        ref={panelRef}
        className="sheet"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
        data-testid={testId}
      >
        <div className="sheet__grip" />
        <div className="sheet__head">
          <h2 className="display display--md">{title}</h2>
          <Button variant="ghost" icon aria-label="Close" onClick={onClose} testId={testId ? `${testId}-close` : undefined}>
            ✕
          </Button>
        </div>
        <div className="sheet__body">{children}</div>
      </div>
    </div>,
    document.body,
  );
}

/* ---------- Toast ---------- */
export type ToastKind = 'info' | 'error' | 'success';
interface ToastItem {
  id: number;
  text: string;
  kind: ToastKind;
}

type ToastListener = (items: ToastItem[]) => void;
let toastItems: ToastItem[] = [];
let toastSeq = 0;
const toastListeners = new Set<ToastListener>();

function emitToasts() {
  toastListeners.forEach((l) => l(toastItems));
}

/** Module-level toast bus: callable from anywhere (actions, drivers) without the store. */
export function toast(text: string, kind: ToastKind = 'info', ms = 2400): void {
  const id = ++toastSeq;
  toastItems = [...toastItems, { id, text, kind }];
  emitToasts();
  setTimeout(() => {
    toastItems = toastItems.filter((t) => t.id !== id);
    emitToasts();
  }, ms);
}

export function ToastHost() {
  const [items, setItems] = useState<ToastItem[]>(toastItems);
  useEffect(() => {
    toastListeners.add(setItems);
    return () => {
      toastListeners.delete(setItems);
    };
  }, []);
  // The container stays mounted even when empty. A live region is only watched once it exists, so
  // one that arrives in the same commit as its first message is never announced — which silently
  // made every toast (including error copy like "Codes are 5 letters and numbers") invisible to a
  // screen reader. Only the children come and go.
  return (
    <div className="toasts" aria-live="polite" data-testid="toasts">
      {items.map((t) => (
        <div key={t.id} className={`toast toast--${t.kind}`}>
          {t.text}
        </div>
      ))}
    </div>
  );
}
